/**
 * Frameworks Overview Page - Complete Implementation
 * Displays framework taxonomy with categories and subcategories
 * 
 * Categories:
 * - Mandatory Reporting (CSRD, CSDDD, EU Taxonomy, SFDR, CBAM)
 * - Voluntary Commitments (TCFD, TNFD, SBTi, GRI, SASB, GHG Protocol, CDP)
 * - Policy Context (Paris Agreement, UN SDGs, EU Green Deal)
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  RefreshCw,
  AlertCircle,
  ArrowRight,
  Star,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  BarChart3,
  FileText,
  Target,
  Scale,
  Leaf,
  Globe,
  TrendingUp
} from 'lucide-react';
import { API } from '../App';
import { apiFetch } from '../services/api_service';
import { JourneySection, JourneyPanel } from './journeys/JourneyPanel';
import { TaxonomyAlignment, CBAMEstimator, OmnibusScope, JurisdictionOverlap } from './journeys/JourneyFrameworks';

// Icon mapping for framework categories
const CATEGORY_ICONS = {
  'disclosure': FileText,
  'methodology': BarChart3,
  'target_setting': Target,
  'due_diligence': Scale,
  'classification': Leaf,
  'trade': Globe,
  'certification': TrendingUp,
  'treaty': Globe,
  'goals': Target,
  'policy_package': FileText
};

export default function FrameworksOverviewPage() {
  const [categories, setCategories] = useState([]);
  const [allFrameworks, setAllFrameworks] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set(['MANDATORY_REPORTING', 'VOLUNTARY_COMMITMENTS']));

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // Fetch categories
        const catData = await apiFetch('/framework-categories/');
        if (catData && !catData._error) {
          setCategories(catData.categories || []);
        }

        // Fetch all frameworks
        const fwData = await apiFetch('/framework-categories/all-frameworks');
        if (fwData && !fwData._error) {
          setAllFrameworks(fwData.frameworks || []);
        }

        // Fetch statistics
        const statsData = await apiFetch('/framework-categories/statistics');
        if (statsData && !statsData._error) {
          setStatistics(statsData);
        }

      } catch (err) {
        console.error('Error fetching frameworks:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Toggle category expansion
  const toggleCategory = (categoryCode) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryCode)) {
      newExpanded.delete(categoryCode);
    } else {
      newExpanded.add(categoryCode);
    }
    setExpandedCategories(newExpanded);
  };

  // Get frameworks for a subcategory
  const getFrameworksForSubcategory = (subcategoryCode) => {
    return allFrameworks.filter(fw => fw.sub_category === subcategoryCode);
  };

  // Get framework URL
  const getFrameworkUrl = (frameworkCode) => {
    const urlMap = {
      'CSRD_ESRS': '/frameworks/csrd-esrs',
      'CSDDD': '/frameworks/csddd',
      'EU_TAXONOMY': '/frameworks/eu-taxonomy',
      'SFDR': '/frameworks/sfdr',
      'CBAM': '/frameworks/cbam',
      'TCFD': '/frameworks/tcfd',
      'TNFD': '/frameworks/tnfd',
      'SBTi': '/frameworks/sbti',
      'GRI': '/frameworks/gri',
      'SASB': '/frameworks/sasb',
      'GHG_PROTOCOL_CORPORATE': '/frameworks/ghg-protocol',
      'GHG_PROTOCOL_SCOPE3': '/frameworks/ghg-protocol-scope3',
      'ISO_14064_1': '/frameworks/iso-14064',
      'CDP_CLIMATE': '/frameworks/cdp',
      'CDP_WATER': '/frameworks/cdp-water',
      'CDP_FORESTS': '/frameworks/cdp-forests',
    };
    return urlMap[frameworkCode] || `/frameworks/${frameworkCode.toLowerCase().replace(/_/g, '-')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="frameworks-loading">
        <div className="flex items-center gap-3">
          <RefreshCw size={24} className="text-emerald-400 animate-spin" />
          <span className="text-[#A0A0A0]">Loading frameworks...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="frameworks-overview-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Frameworks</h1>
        <p className="text-[#6B6B6B]">
          Navigate ESG reporting standards and compliance requirements
        </p>
      </div>

      {/* Statistics Summary */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard 
            label="Total Frameworks" 
            value={statistics.total_frameworks} 
            color="emerald"
          />
          <StatCard 
            label="Mandatory" 
            value={statistics.by_type.mandatory} 
            color="red"
            icon="🔴"
          />
          <StatCard 
            label="Voluntary" 
            value={statistics.by_type.voluntary} 
            color="green"
            icon="🟢"
          />
          <StatCard 
            label="Total Requirements" 
            value={statistics.total_requirements_across_all_frameworks?.toLocaleString()} 
            color="blue"
          />
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <div className="flex items-center gap-2 text-amber-400 text-sm">
            <AlertCircle size={16} />
            <span>Error loading frameworks: {error}</span>
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="space-y-6">
        {categories.map(category => (
          <CategorySection
            key={category.category_code}
            category={category}
            expanded={expandedCategories.has(category.category_code)}
            onToggle={() => toggleCategory(category.category_code)}
            getFrameworksForSubcategory={getFrameworksForSubcategory}
            getFrameworkUrl={getFrameworkUrl}
          />
        ))}
      </div>

      {/* Quick Links */}
      <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Access</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <QuickLink
            title="CSRD/ESRS"
            description="EU mandatory reporting with 1,144 data points"
            href="/frameworks/csrd-esrs"
            featured
          />
          <QuickLink
            title="GHG Protocol Scope 3"
            description="15 value chain emission categories"
            href="/frameworks/ghg-protocol-scope3"
          />
          <QuickLink
            title="Cross-Framework Map"
            description="See how requirements overlap"
            href="/evidence/cross-framework"
          />
        </div>
      </div>

      {/* Live User Journeys */}
      <JourneySection title="Live Journeys">
        <JourneyPanel title="EU Taxonomy Eligibility & Alignment" ucId="UC-17"><TaxonomyAlignment /></JourneyPanel>
        <JourneyPanel title="CBAM Carbon Border Estimator" ucId="UC-22"><CBAMEstimator /></JourneyPanel>
        <JourneyPanel title="Omnibus I Scope Check" ucId="UC-16"><OmnibusScope /></JourneyPanel>
        <JourneyPanel title="Multi-Jurisdiction Overlap Analysis" ucId="UC-30"><JurisdictionOverlap /></JourneyPanel>
      </JourneySection>
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, color, icon }) {
  const colorClasses = {
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    red: 'bg-red-500/10 border-red-500/20 text-red-400',
    green: 'bg-green-500/10 border-green-500/20 text-green-400',
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-400'
  };

  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        {icon && <span>{icon}</span>}
        <span className="text-xs font-medium text-[#6B6B6B]">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

// Category Section Component
function CategorySection({ category, expanded, onToggle, getFrameworksForSubcategory, getFrameworkUrl }) {
  return (
    <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl overflow-hidden">
      {/* Category Header */}
      <button
        onClick={onToggle}
        className="w-full p-6 flex items-center justify-between hover:bg-[#111111] transition-colors"
        data-testid={`category-${category.category_code}`}
      >
        <div className="flex items-center gap-4">
          <span className="text-2xl">{category.category_icon}</span>
          <div className="text-left">
            <h2 className="text-xl font-semibold text-white">{category.category_name}</h2>
            <p className="text-sm text-[#6B6B6B]">{category.category_description}</p>
          </div>
        </div>
        {expanded ? (
          <ChevronDown size={24} className="text-[#6B6B6B]" />
        ) : (
          <ChevronRight size={24} className="text-[#6B6B6B]" />
        )}
      </button>

      {/* Subcategories */}
      {expanded && (
        <div className="border-t border-[#1A1A1A]">
          {category.subcategories?.map(subcat => {
            const frameworks = getFrameworksForSubcategory(subcat.code);
            
            return (
              <div key={subcat.code} className="p-6 border-b border-[#1A1A1A] last:border-b-0">
                {/* Subcategory Header */}
                <div className="flex items-center gap-3 mb-4">
                  {subcat.icon && <span className="text-lg">{subcat.icon}</span>}
                  <h3 className="text-base font-semibold text-white">{subcat.name}</h3>
                  {subcat.featured && (
                    <Star size={14} className="text-amber-400 fill-amber-400" />
                  )}
                  <span className="ml-auto text-xs text-[#6B6B6B]">
                    {frameworks.length} framework{frameworks.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {subcat.description && (
                  <p className="text-sm text-[#6B6B6B] mb-4 ml-8">{subcat.description}</p>
                )}

                {/* Framework Cards */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 ml-8">
                  {frameworks.map(fw => (
                    <FrameworkCard
                      key={fw.framework_code}
                      framework={fw}
                      href={getFrameworkUrl(fw.framework_code)}
                      featured={subcat.featured}
                    />
                  ))}

                  {/* Show placeholder cards from subcategory definition if no frameworks found */}
                  {frameworks.length === 0 && subcat.frameworks?.map(fwCode => (
                    <FrameworkCard
                      key={fwCode}
                      framework={{ framework_code: fwCode, framework_short_name: fwCode }}
                      href={getFrameworkUrl(fwCode)}
                      featured={subcat.featured}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Framework Card Component
function FrameworkCard({ framework, href, featured }) {
  const totalReqs = framework.requirements_summary?.total_data_points || 0;
  const Icon = CATEGORY_ICONS[framework.framework_category] || FileText;

  return (
    <Link
      to={href}
      className={`block p-4 rounded-lg border transition-all hover:shadow-lg group ${
        featured 
          ? 'border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/50' 
          : 'border-[#1A1A1A] bg-[#0A0A0A] hover:border-[#2A2A2A]'
      }`}
      data-testid={`framework-card-${framework.framework_code}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${featured ? 'bg-emerald-500/20' : 'bg-[#1A1A1A]'}`}>
            <Icon size={14} className={featured ? 'text-emerald-400' : 'text-[#6B6B6B]'} />
          </div>
          <h4 className="text-sm font-semibold text-white group-hover:text-emerald-400 transition-colors">
            {framework.framework_short_name || framework.framework_code}
          </h4>
        </div>
        <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${
          framework.framework_type === 'mandatory' 
            ? 'bg-red-500/20 text-red-400' 
            : framework.framework_type === 'voluntary'
              ? 'bg-green-500/20 text-green-400'
              : 'bg-blue-500/20 text-blue-400'
        }`}>
          {framework.framework_type}
        </span>
      </div>

      {totalReqs > 0 && (
        <p className="text-xs text-[#6B6B6B] mb-2">
          {totalReqs.toLocaleString()} requirements
        </p>
      )}

      <div className="flex items-center justify-end text-xs text-emerald-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
        View details
        <ArrowRight size={12} className="ml-1" />
      </div>
    </Link>
  );
}

// Quick Link Component
function QuickLink({ title, description, href, featured }) {
  return (
    <Link
      to={href}
      className={`block p-4 rounded-lg border transition-all hover:shadow-lg group ${
        featured 
          ? 'border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/50' 
          : 'border-[#1A1A1A] hover:border-[#2A2A2A]'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-white group-hover:text-emerald-400 transition-colors">
          {title}
        </h4>
        <ArrowRight size={14} className="text-[#4A4A4A] group-hover:text-emerald-400 transition-colors" />
      </div>
      <p className="text-xs text-[#6B6B6B]">{description}</p>
    </Link>
  );
}
